// Mercapoli - Gestión de Productos del Comercio
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, Edit, Trash2, Image,
  Package, DollarSign, Eye, EyeOff, AlertCircle,
  Upload, X, Check, MoreVertical, ShoppingBag, Store
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { comercio as comercioApi } from '../../lib/api';
import { toast } from 'sonner';
import MerchantHeader from '../../components/merchant/MerchantHeader';

const MerchantProductos = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState({ categorias_comercio: [], categorias_globales: [] });
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [busqueda, setBusqueda] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    precio_base: '',
    precio_promocional: '',
    categoria_id: '',
    etiquetas: [],
    tiempo_preparacion_min: 15,
    estado: 'activo',
    stock: '',
    imagen_principal: ''
  });

  const loadData = async () => {
    try {
      const params = {};
      if (filtroEstado) params.estado = filtroEstado;
      if (filtroCategoria) params.categoria_id = filtroCategoria;
      
      const [productosData, categoriasData] = await Promise.all([
        comercioApi.getProductos(params),
        comercioApi.getCategorias()
      ]);
      setProductos(productosData);
      setCategorias(categoriasData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar productos');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEstado, filtroCategoria]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.precio_base) {
      toast.error('Nombre y precio son requeridos');
      return;
    }

    try {
      const data = {
        ...formData,
        precio_base: parseFloat(formData.precio_base),
        precio_promocional: formData.precio_promocional ? parseFloat(formData.precio_promocional) : null,
        stock: formData.stock ? parseInt(formData.stock) : null,
        tiempo_preparacion_min: parseInt(formData.tiempo_preparacion_min)
      };

      if (editingProduct) {
        await comercioApi.updateProducto(editingProduct.producto_id, data);
        toast.success('Producto actualizado');
      } else {
        // Crear producto primero
        const nuevoProducto = await comercioApi.createProducto(data);
        
        // Si hay imagen seleccionada, subirla
        if (selectedFile && nuevoProducto.producto_id) {
          setUploading(true);
          try {
            await comercioApi.subirImagenProducto(nuevoProducto.producto_id, selectedFile, true);
            toast.success('Producto creado con imagen');
          } catch (imgError) {
            console.error('Error subiendo imagen:', imgError);
            toast.success('Producto creado (imagen pendiente)');
          }
          setUploading(false);
        } else {
          toast.success('Producto creado');
        }
      }
      
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al guardar producto');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo
      if (!file.type.match(/image\/(jpeg|jpg|png|webp)/)) {
        toast.error('Solo se permiten imágenes JPG, PNG o WebP');
        return;
      }
      // Validar tamaño (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('La imagen no debe superar 5MB');
        return;
      }
      setSelectedFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleDelete = async (productoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;
    
    try {
      await comercioApi.deleteProducto(productoId);
      toast.success('Producto eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const handleEstadoChange = async (productoId, nuevoEstado) => {
    try {
      await comercioApi.cambiarEstadoProducto(productoId, nuevoEstado);
      toast.success(`Producto ${nuevoEstado === 'activo' ? 'activado' : nuevoEstado}`);
      loadData();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleImageUpload = async (e, productoId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }

    setUploading(true);
    try {
      const result = await comercioApi.subirImagenProducto(productoId, file, true);
      toast.success('Imagen subida');
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al subir imagen');
    }
    setUploading(false);
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      precio_base: '',
      precio_promocional: '',
      categoria_id: '',
      etiquetas: [],
      tiempo_preparacion_min: 15,
      estado: 'activo',
      stock: '',
      imagen_principal: ''
    });
    setEditingProduct(null);
    setSelectedFile(null);
    setImagePreview(null);
  };

  const openEditModal = (producto) => {
    setEditingProduct(producto);
    setFormData({
      nombre: producto.nombre,
      descripcion: producto.descripcion || '',
      precio_base: producto.precio_base?.toString() || '',
      precio_promocional: producto.precio_promocional?.toString() || '',
      categoria_id: producto.categoria_id || '',
      etiquetas: producto.etiquetas || [],
      tiempo_preparacion_min: producto.tiempo_preparacion_min || 15,
      estado: producto.estado || 'activo',
      stock: producto.stock?.toString() || '',
      imagen_principal: producto.imagen_principal || ''
    });
    setImagePreview(producto.imagen_principal || null);
    setSelectedFile(null);
    setShowModal(true);
  };

  const getEstadoBadge = (estado) => {
    const styles = {
      activo: 'bg-green-100 text-green-800',
      pausado: 'bg-yellow-100 text-yellow-800',
      agotado: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[estado] || 'bg-gray-100'}>{estado}</Badge>;
  };

  const filteredProducts = productos.filter(p => 
    p.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
    p.descripcion?.toLowerCase().includes(busqueda.toLowerCase())
  );

  const allTags = ['nuevo', 'popular', 'vegano', 'picante', 'sin_gluten', 'organico'];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header con Logout */}
      <MerchantHeader 
        title="Productos" 
        rightContent={
          <Button onClick={() => { resetForm(); setShowModal(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Nuevo
          </Button>
        }
      />

      <main className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar productos..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filtroEstado || "all"} onValueChange={(v) => setFiltroEstado(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="activo">Activos</SelectItem>
              <SelectItem value="pausado">Pausados</SelectItem>
              <SelectItem value="agotado">Agotados</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filtroCategoria || "all"} onValueChange={(v) => setFiltroCategoria(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Categoría" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {categorias.categorias_comercio?.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{productos.length}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{productos.filter(p => p.estado === 'activo').length}</p>
              <p className="text-xs text-muted-foreground">Activos</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{productos.filter(p => p.estado === 'agotado').length}</p>
              <p className="text-xs text-muted-foreground">Agotados</p>
            </CardContent>
          </Card>
        </div>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <ShoppingBag className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No hay productos</p>
              <Button className="mt-4" onClick={() => { resetForm(); setShowModal(true); }}>
                <Plus className="h-4 w-4 mr-2" /> Crear primer producto
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence>
              {filteredProducts.map((producto) => (
                <motion.div
                  key={producto.producto_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <Card className="overflow-hidden">
                    {/* Image */}
                    <div className="relative aspect-video bg-muted">
                      {producto.imagen_principal ? (
                        <img
                          src={producto.imagen_principal}
                          alt={producto.nombre}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <Package className="h-12 w-12 text-muted-foreground opacity-30" />
                        </div>
                      )}
                      <div className="absolute top-2 right-2">
                        {getEstadoBadge(producto.estado)}
                      </div>
                      {producto.tiene_promocion && (
                        <Badge className="absolute top-2 left-2 bg-red-500">Promo</Badge>
                      )}
                      
                      {/* Upload overlay */}
                      <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageUpload(e, producto.producto_id)}
                          disabled={uploading}
                        />
                        <div className="text-white text-center">
                          <Upload className="h-8 w-8 mx-auto mb-2" />
                          <span className="text-sm">{uploading ? 'Subiendo...' : 'Cambiar imagen'}</span>
                        </div>
                      </label>
                    </div>

                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium truncate">{producto.nombre}</h3>
                          <p className="text-xs text-muted-foreground line-clamp-1">{producto.descripcion}</p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditModal(producto)}>
                              <Edit className="h-4 w-4 mr-2" /> Editar
                            </DropdownMenuItem>
                            {producto.estado !== 'activo' && (
                              <DropdownMenuItem onClick={() => handleEstadoChange(producto.producto_id, 'activo')}>
                                <Eye className="h-4 w-4 mr-2" /> Activar
                              </DropdownMenuItem>
                            )}
                            {producto.estado !== 'pausado' && (
                              <DropdownMenuItem onClick={() => handleEstadoChange(producto.producto_id, 'pausado')}>
                                <EyeOff className="h-4 w-4 mr-2" /> Pausar
                              </DropdownMenuItem>
                            )}
                            {producto.estado !== 'agotado' && (
                              <DropdownMenuItem onClick={() => handleEstadoChange(producto.producto_id, 'agotado')}>
                                <AlertCircle className="h-4 w-4 mr-2" /> Agotado
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(producto.producto_id)}>
                              <Trash2 className="h-4 w-4 mr-2" /> Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          {producto.precio_promocional && producto.precio_promocional < producto.precio_base ? (
                            <>
                              <span className="text-lg font-bold text-green-600">Q{producto.precio_promocional.toFixed(2)}</span>
                              <span className="text-sm text-muted-foreground line-through ml-2">Q{producto.precio_base.toFixed(2)}</span>
                            </>
                          ) : (
                            <span className="text-lg font-bold">Q{producto.precio_base?.toFixed(2)}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Package className="h-3 w-3" />
                          <span>{producto.total_ventas || 0} vendidos</span>
                        </div>
                      </div>

                      {producto.etiquetas?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {producto.etiquetas.map((tag) => (
                            <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Ej: Hamburguesa Clásica"
                required
              />
            </div>

            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Descripción del producto..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Precio Base *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.precio_base}
                  onChange={(e) => setFormData({ ...formData, precio_base: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <Label>Precio Promocional</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.precio_promocional}
                  onChange={(e) => setFormData({ ...formData, precio_promocional: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoría</Label>
                <Select
                  value={formData.categoria_id}
                  onValueChange={(v) => setFormData({ ...formData, categoria_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {categorias.categorias_comercio?.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                    {categorias.categorias_globales?.map((cat) => (
                      <SelectItem key={cat.categoria_id} value={cat.categoria_id}>{cat.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tiempo Prep. (min)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.tiempo_preparacion_min}
                  onChange={(e) => setFormData({ ...formData, tiempo_preparacion_min: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Stock (opcional)</Label>
              <Input
                type="number"
                min="0"
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                placeholder="Dejar vacío para sin límite"
              />
            </div>

            {/* Image upload section */}
            <div>
              <Label>Imagen del Producto</Label>
              <div className="mt-2 border-2 border-dashed border-muted-foreground/25 rounded-lg p-4">
                {imagePreview ? (
                  <div className="relative">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-40 object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => {
                        setImagePreview(null);
                        setSelectedFile(null);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-32 cursor-pointer hover:bg-muted/50 rounded-lg transition-colors">
                    <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                    <span className="text-sm text-muted-foreground">Click para subir imagen</span>
                    <span className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP (max 5MB)</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                  </label>
                )}
              </div>
              {!editingProduct && !selectedFile && (
                <p className="text-xs text-muted-foreground mt-2">
                  También puedes agregar la imagen después de crear el producto.
                </p>
              )}
            </div>

            <div>
              <Label>Etiquetas</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {allTags.map((tag) => (
                  <Badge
                    key={tag}
                    variant={formData.etiquetas.includes(tag) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => {
                      const newTags = formData.etiquetas.includes(tag)
                        ? formData.etiquetas.filter(t => t !== tag)
                        : [...formData.etiquetas, tag];
                      setFormData({ ...formData, etiquetas: newTags });
                    }}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Estado</Label>
              <Select
                value={formData.estado}
                onValueChange={(v) => setFormData({ ...formData, estado: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="pausado">Pausado</SelectItem>
                  <SelectItem value="agotado">Agotado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MerchantProductos;
